import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Divider,
    TextField,
    Autocomplete,
    Chip,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";

import queryGraphql from "../../../helpers/ApiInterface"; // Assuming path
import { ExpandMore } from "@mui/icons-material";
import MainDrawer from "../../MainDrawer"; // Assuming path
import NotebooksRequestTable from "./NotebooksRequestsTable"; // Import new table
import MiniBot from "../../MiniBot"; // Assuming path

// Interface for search options, tailored for Notebooks
interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: string;
    groupBy: string;
}

// Interface for the processed notebook data structure used in this page
interface ProcessedNotebook {
    id: string;
    notebookName: string;
    cloudProvider?: string;
    applicationName?: string;
    status: string;
    createdAt: string | null;
    actionedAt: string | null;
    notebookOwnerName: string | null | undefined;
    notebookOwnerAvatar?: string;
    notebookApproverName: string | null | undefined;
    notebookApproverAvatar?: string;
    reasonForRejection?: string;
}

let searchOptions: SearchOptions[] = [];

const NotebooksRequestsPage: React.FC = () => {
    const [notebooks, setNotebooks] = useState<ProcessedNotebook[]>([]);
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const navigate = useNavigate();

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                // GraphQL Query to get notebooks, with application field
                const data = await queryGraphql(`
                    query GetNotebooks {
                      getNotebooks {
                        id
                        name
                        cloudProvider
                        application {
                           id
                           applicationName
                        }
                        status
                        reasonForRejection
                        createdAt
                        updatedAt
                        solutionOwner {
                          id
                          name
                          googleProfilePicture
                        }
                        solutionApprover {
                          id
                          name
                          googleProfilePicture
                        }
                      }
                    }
                `);

                const rawNotebooksData = data.getNotebooks || data.getNotebooks || [];

                // Process the raw data into the structure needed for the requests view
                let notebooksArr: ProcessedNotebook[] = rawNotebooksData.map((nb: any) => {
                    return {
                        id: nb.id,
                        notebookName: nb.name,
                        cloudProvider: nb.cloudProvider,
                        applicationName: nb.application?.applicationName,
                        status: nb.status ? nb.status.toLowerCase() : 'pending',
                        createdAt: nb.createdAt ? new Date(Number(nb.createdAt)).toISOString() : null,
                        actionedAt: nb.updatedAt ? new Date(Number(nb.updatedAt)).toISOString() : null,
                        notebookOwnerName: nb.solutionOwner.name,
                        notebookOwnerAvatar: nb.solutionOwner.googleProfilePicture, 
                        notebookApproverName: nb.solutionApprover?.name,
                        notebookApproverAvatar: nb.solutionApprover?.googleProfilePicture,
                        reasonForRejection: nb.reasonForRejection,
                    };
                });
                setNotebooks(notebooksArr);

                // Generate search options from the processed notebook data
                searchOptions = notebooksArr
                    .map((nb: ProcessedNotebook) => {
                        const options = [
                            {
                                category: "notebookName",
                                groupBy: "NOTEBOOK NAME",
                                chipLabel: `Notebook: ${nb.notebookName}`,
                                dropdownLabel: nb.notebookName,
                            },
                        ];
                        if (nb.cloudProvider) {
                            options.push({
                                category: "cloudProvider",
                                groupBy: "CLOUD PROVIDER",
                                chipLabel: `Provider: ${nb.cloudProvider}`,
                                dropdownLabel: nb.cloudProvider,
                            });
                        }
                        if (nb.applicationName) {
                            options.push({
                                category: "applicationName",
                                groupBy: "APPLICATION",
                                chipLabel: `App: ${nb.applicationName}`,
                                dropdownLabel: nb.applicationName,
                            });
                        }
                        if (nb.notebookOwnerName) {
                            options.push({
                                category: "notebookOwnerName",
                                groupBy: "OWNER",
                                chipLabel: `Owner: ${nb.notebookOwnerName}`,
                                dropdownLabel: nb.notebookOwnerName,
                            });
                        }
                        if (nb.notebookApproverName) {
                            options.push({
                                category: "notebookApproverName",
                                groupBy: "APPROVER",
                                chipLabel: `Approver: ${nb.notebookApproverName}`,
                                dropdownLabel: nb.notebookApproverName,
                            });
                        }
                        if (nb.status) {
                            options.push({
                                category: "status",
                                groupBy: "STATUS",
                                chipLabel: `Status: ${nb.status.charAt(0).toUpperCase() + nb.status.slice(1)}`,
                                dropdownLabel: nb.status.charAt(0).toUpperCase() + nb.status.slice(1),
                            });
                        }
                        return options;
                    })
                    .flat();

                // Deduplicate search options
                searchOptions = searchOptions.filter(
                    (obj, index, self) =>
                        obj.dropdownLabel &&
                        index ===
                        self.findIndex(
                            (t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
                        )
                );

                // Sort search options
                searchOptions.sort((a, b) => {
                    if (a.groupBy > b.groupBy) return 1;
                    if (a.groupBy < b.groupBy) return -1;
                    if (a.dropdownLabel > b.dropdownLabel) return 1;
                    if (a.dropdownLabel < b.dropdownLabel) return -1;
                    return 0;
                });

            } catch (error) {
                console.log("Error fetching notebooks for request page:", error);
            }
        };

        fetchData();
    }, []);

    // Filter notebooks based on the search query
    const filteredNotebooks =
        searchQuery.length === 0
            ? notebooks
            : notebooks.filter(
                (notebook: ProcessedNotebook) =>
                    (searchQuery.filter((e) => e.category === "notebookName").length > 0
                        ? searchQuery
                                .filter((e) => e.category === "notebookName")
                                .map((e) => e.dropdownLabel?.toLowerCase())
                                .includes(notebook.notebookName?.toLowerCase())
                        : true) &&
                    (searchQuery.filter((e) => e.category === "cloudProvider").length > 0
                        ? (notebook.cloudProvider &&
                            searchQuery
                                .filter((e) => e.category === "cloudProvider")
                                .map((e) => e.dropdownLabel?.toLowerCase())
                                .includes(notebook.cloudProvider.toLowerCase()))
                        : true) &&
                    (searchQuery.filter((e) => e.category === "applicationName").length > 0
                        ? (notebook.applicationName &&
                            searchQuery
                                .filter((e) => e.category === "applicationName")
                                .map((e) => e.dropdownLabel?.toLowerCase())
                                .includes(notebook.applicationName.toLowerCase()))
                        : true) &&
                    (searchQuery.filter((e) => e.category === "notebookOwnerName").length > 0
                        ? (notebook.notebookOwnerName &&
                            searchQuery
                                .filter((e) => e.category === "notebookOwnerName")
                                .map((e) => e.dropdownLabel?.toLowerCase())
                                .includes(notebook.notebookOwnerName.toLowerCase()))
                        : true
                    ) &&
                    (searchQuery.filter((e) => e.category === "notebookApproverName").length > 0
                        ? (notebook.notebookApproverName &&
                            searchQuery
                                .filter((e) => e.category === "notebookApproverName")
                                .map((e) => e.dropdownLabel?.toLowerCase())
                                .includes(notebook.notebookApproverName.toLowerCase()))
                        : true
                    ) &&
                    (searchQuery.filter((e) => e.category === "status").length > 0
                        ? searchQuery
                                .filter((e) => e.category === "status")
                                .map((e) => e.dropdownLabel?.toLowerCase())
                                .includes(notebook.status?.toLowerCase())
                        : true)
            );

    // Group filtered notebooks by status for accordions
    const pendingStatuses = ['pending', 'submitted', 'provisioning'];
    const activeStatuses = ['active', 'approved', 'success'];
    const rejectedStatuses = ['rejected', 'failed'];

    const filteredPendingNotebooks = filteredNotebooks.filter(
        (nb: ProcessedNotebook) => pendingStatuses.includes(nb.status)
    );
    const filteredActiveNotebooks = filteredNotebooks.filter(
        (nb: ProcessedNotebook) => activeStatuses.includes(nb.status)
    );
    const filteredRejectedNotebooks = filteredNotebooks.filter(
        (nb: ProcessedNotebook) => rejectedStatuses.includes(nb.status)
    );

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"requests-notebooks"} />
            <MiniBot />
            <Box
                component="main"
                sx={{
                    flex: 1,
                    bgcolor: "#fafafa",
                    minHeight: "100vh",
                }}
            >
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={2} mb="18px">
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginY: 1,
                                marginLeft: 2,
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">
                                Requests
                            </Typography>
                             <IconButton
                                aria-label="Help"
                                onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                sx={{
                                    padding: 0, border: 'none',  mr:2,
                                    '&:hover': { backgroundColor: 'action.hover' },
                                }}
                              >
                                <Box component="img" src="/assets/Help.svg" alt="Help Icon" sx={{ width: 40, height: 40, display: 'block' }} />
                            </IconButton>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box
                            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2 }}
                        >
                            <Box>
                                <Typography
                                    fontFamily="Gilroy-SemiBold"
                                    fontSize="16px"
                                >{`NOTEBOOKS (${notebooks.length})`}</Typography>
                                <Typography
                                    fontFamily="Gilroy-Regular"
                                    fontSize="14px"
                                >{`View all the requests raised for Notebooks`}</Typography>
                            </Box>
                            <Autocomplete
                                multiple
                                options={searchOptions}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                defaultValue={[]}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) => {
                                    return tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />
                                    ));
                                }}
                                onChange={(e, newVal) => {
                                    setSearchQuery(newVal);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by notebook, owner, status etc"
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <img
                                                            src="/assets/search-normal.svg"
                                                            alt="Search Icon"
                                                            style={{ width: "16px", paddingLeft: "4px" }}
                                                        />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: {
                                                backgroundColor: "#f4f4f4",
                                                minHeight: "45px",
                                                width: "500px",
                                                borderRadius: "8px",
                                                fontFamily: "Gilroy-Regular",
                                                fontSize: "14px",
                                            },
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Box>
                    <Box m={2}>
                        <Accordion
                            defaultExpanded={filteredPendingNotebooks.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredPendingNotebooks.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }}/>
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Pending Notebooks ({filteredPendingNotebooks.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredPendingNotebooks.length > 0 && (
                                    <NotebooksRequestTable notebooks={filteredPendingNotebooks} />
                                )}
                            </AccordionDetails>
                        </Accordion>
                        
                        <Accordion
                            defaultExpanded={true}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredActiveNotebooks.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Active Notebooks ({filteredActiveNotebooks.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredActiveNotebooks.length > 0 && (
                                    <NotebooksRequestTable notebooks={filteredActiveNotebooks} />
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded={filteredRejectedNotebooks.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={filteredRejectedNotebooks.length === 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Rejected Notebooks ({filteredRejectedNotebooks.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {filteredRejectedNotebooks.length > 0 && (
                                    <NotebooksRequestTable notebooks={filteredRejectedNotebooks} />
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default NotebooksRequestsPage;



// import React, { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Container,
//     Typography,
//     Divider,
//     TextField,
//     Autocomplete,
//     Chip,
//     InputAdornment,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     IconButton,
// } from "@mui/material";

// import queryGraphql from "../../../helpers/ApiInterface"; // Assuming path
// import { ExpandMore } from "@mui/icons-material";
// import MainDrawer from "../../MainDrawer"; // Assuming path
// import NotebooksRequestTable from "./NotebooksRequestsTable"; // Import new table
// import MiniBot from "../../MiniBot"; // Assuming path

// // Interface for search options, tailored for Notebooks
// interface SearchOptions {
//     chipLabel: string;
//     dropdownLabel: string;
//     category: string;
//     groupBy: string;
// }

// // Interface for the processed notebook data structure used in this page
// interface ProcessedNotebook {
//     id: string;
//     notebookName: string;
//     applicationName?: string;
//     status: string;
//     createdAt: string | null;
//     actionedAt: string | null;
//     notebookOwnerName: string | null | undefined;
//     notebookOwnerAvatar?: string;
//     notebookApproverName: string | null | undefined;
//     reasonForRejection?: string;
// }

// let searchOptions: SearchOptions[] = [];

// const NotebooksRequestsPage: React.FC = () => {
//     const [notebooks, setNotebooks] = useState<ProcessedNotebook[]>([]);
//     const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
//     const navigate = useNavigate();

//     React.useEffect(() => {
//         const fetchData = async () => {
//             try {
//                 // GraphQL Query to get notebooks, with application field
//                 const data = await queryGraphql(`
//                     query GetNotebooks {
//                       getNotebooks {
//                         id
//                         name
//                         application {
//                            id
//                            applicationName
//                         }
//                         status
//                         reasonForRejection
//                         createdAt
//                         updatedAt
//                         solutionOwner {
//                           id
//                           name
//                         }
//                         solutionApprover {
//                           id
//                           name
//                         }
//                       }
//                     }
//                 `);

//                 const rawNotebooksData = data.getNotebooks || data.getNotebooks || [];

//                 // Process the raw data into the structure needed for the requests view
//                 let notebooksArr: ProcessedNotebook[] = rawNotebooksData.map((nb: any) => {
//                     return {
//                         id: nb.id,
//                         notebookName: nb.name,
//                         applicationName: nb.application?.applicationName,
//                         status: nb.status ? nb.status.toLowerCase() : 'pending',
//                         createdAt: nb.createdAt ? new Date(Number(nb.createdAt)).toISOString() : null,
//                         actionedAt: nb.updatedAt ? new Date(Number(nb.updatedAt)).toISOString() : null,
//                         notebookOwnerName: nb.solutionOwner.name,
//                         notebookOwnerAvatar: undefined,
//                         notebookApproverName: nb.solutionApprover?.name,
//                         reasonForRejection: nb.reasonForRejection,
//                     };
//                 });
//                 setNotebooks(notebooksArr);

//                 // Generate search options from the processed notebook data
//                 searchOptions = notebooksArr
//                     .map((nb: ProcessedNotebook) => {
//                         const options = [
//                             {
//                                 category: "notebookName",
//                                 groupBy: "NOTEBOOK NAME",
//                                 chipLabel: `Notebook: ${nb.notebookName}`,
//                                 dropdownLabel: nb.notebookName,
//                             },
//                         ];
//                         if (nb.applicationName) {
//                             options.push({
//                                 category: "applicationName",
//                                 groupBy: "APPLICATION",
//                                 chipLabel: `App: ${nb.applicationName}`,
//                                 dropdownLabel: nb.applicationName,
//                             });
//                         }
//                         if (nb.notebookOwnerName) {
//                             options.push({
//                                 category: "notebookOwnerName",
//                                 groupBy: "OWNER",
//                                 chipLabel: `Owner: ${nb.notebookOwnerName}`,
//                                 dropdownLabel: nb.notebookOwnerName,
//                             });
//                         }
//                         if (nb.notebookApproverName) {
//                             options.push({
//                                 category: "notebookApproverName",
//                                 groupBy: "APPROVER",
//                                 chipLabel: `Approver: ${nb.notebookApproverName}`,
//                                 dropdownLabel: nb.notebookApproverName,
//                             });
//                         }
//                         if (nb.status) {
//                             options.push({
//                                 category: "status",
//                                 groupBy: "STATUS",
//                                 chipLabel: `Status: ${nb.status.charAt(0).toUpperCase() + nb.status.slice(1)}`,
//                                 dropdownLabel: nb.status.charAt(0).toUpperCase() + nb.status.slice(1),
//                             });
//                         }
//                         return options;
//                     })
//                     .flat();

//                 // Deduplicate search options
//                 searchOptions = searchOptions.filter(
//                     (obj, index, self) =>
//                         obj.dropdownLabel &&
//                         index ===
//                         self.findIndex(
//                             (t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
//                         )
//                 );

//                 // Sort search options
//                 searchOptions.sort((a, b) => {
//                     if (a.groupBy > b.groupBy) return 1;
//                     if (a.groupBy < b.groupBy) return -1;
//                     if (a.dropdownLabel > b.dropdownLabel) return 1;
//                     if (a.dropdownLabel < b.dropdownLabel) return -1;
//                     return 0;
//                 });

//             } catch (error) {
//                 console.log("Error fetching notebooks for request page:", error);
//             }
//         };

//         fetchData();
//     }, []);

//     // Filter notebooks based on the search query
//     const filteredNotebooks =
//         searchQuery.length === 0
//             ? notebooks
//             : notebooks.filter(
//                 (notebook: ProcessedNotebook) =>
//                     (searchQuery.filter((e) => e.category === "notebookName").length > 0
//                         ? searchQuery
//                                 .filter((e) => e.category === "notebookName")
//                                 .map((e) => e.dropdownLabel?.toLowerCase())
//                                 .includes(notebook.notebookName?.toLowerCase())
//                         : true) &&
//                     (searchQuery.filter((e) => e.category === "applicationName").length > 0
//                         ? (notebook.applicationName &&
//                             searchQuery
//                                 .filter((e) => e.category === "applicationName")
//                                 .map((e) => e.dropdownLabel?.toLowerCase())
//                                 .includes(notebook.applicationName.toLowerCase()))
//                         : true) &&
//                     (searchQuery.filter((e) => e.category === "notebookOwnerName").length > 0
//                         ? (notebook.notebookOwnerName &&
//                             searchQuery
//                                 .filter((e) => e.category === "notebookOwnerName")
//                                 .map((e) => e.dropdownLabel?.toLowerCase())
//                                 .includes(notebook.notebookOwnerName.toLowerCase()))
//                         : true
//                     ) &&
//                     (searchQuery.filter((e) => e.category === "notebookApproverName").length > 0
//                         ? (notebook.notebookApproverName &&
//                             searchQuery
//                                 .filter((e) => e.category === "notebookApproverName")
//                                 .map((e) => e.dropdownLabel?.toLowerCase())
//                                 .includes(notebook.notebookApproverName.toLowerCase()))
//                         : true
//                     ) &&
//                     (searchQuery.filter((e) => e.category === "status").length > 0
//                         ? searchQuery
//                                 .filter((e) => e.category === "status")
//                                 .map((e) => e.dropdownLabel?.toLowerCase())
//                                 .includes(notebook.status?.toLowerCase())
//                         : true)
//             );

//     // Group filtered notebooks by status for accordions
//     const pendingStatuses = ['pending', 'submitted', 'provisioning'];
//     const activeStatuses = ['active', 'approved', 'success'];
//     const rejectedStatuses = ['rejected', 'failed'];

//     const filteredPendingNotebooks = filteredNotebooks.filter(
//         (nb: ProcessedNotebook) => pendingStatuses.includes(nb.status)
//     );
//     const filteredActiveNotebooks = filteredNotebooks.filter(
//         (nb: ProcessedNotebook) => activeStatuses.includes(nb.status)
//     );
//     const filteredRejectedNotebooks = filteredNotebooks.filter(
//         (nb: ProcessedNotebook) => rejectedStatuses.includes(nb.status)
//     );

//     return (
//         <Box sx={{ display: "flex" }}>
//             <MainDrawer index={"requests-notebooks"} />
//             <MiniBot />
//             <Box
//                 component="main"
//                 sx={{
//                     flex: 1,
//                     bgcolor: "#fafafa",
//                     minHeight: "100vh",
//                 }}
//             >
//                 <Container maxWidth={false} disableGutters>
//                     <Box bgcolor="#FFFFFF" p={2} mb="18px">
//                         <Box
//                             sx={{
//                                 display: "flex",
//                                 justifyContent: "space-between",
//                                 alignItems: "center",
//                                 marginY: 1,
//                                 marginLeft: 2,
//                             }}
//                         >
//                             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">
//                                 Requests
//                             </Typography>
//                              <IconButton
//                                 aria-label="Help"
//                                 onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
//                                 sx={{
//                                     padding: 0, border: 'none',  mr:2,
//                                     '&:hover': { backgroundColor: 'action.hover' },
//                                 }}
//                               >
//                                 <Box component="img" src="/assets/Help.svg" alt="Help Icon" sx={{ width: 40, height: 40, display: 'block' }} />
//                             </IconButton>
//                         </Box>
//                         <Divider sx={{ marginY: 1 }} />
//                         <Box
//                             sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2 }}
//                         >
//                             <Box>
//                                 <Typography
//                                     fontFamily="Gilroy-SemiBold"
//                                     fontSize="16px"
//                                 >{`NOTEBOOKS (${notebooks.length})`}</Typography>
//                                 <Typography
//                                     fontFamily="Gilroy-Regular"
//                                     fontSize="14px"
//                                 >{`View all the requests raised for Notebooks`}</Typography>
//                             </Box>
//                             <Autocomplete
//                                 multiple
//                                 options={searchOptions}
//                                 filterSelectedOptions
//                                 getOptionLabel={(option) => option.dropdownLabel}
//                                 defaultValue={[]}
//                                 groupBy={(option) => option.groupBy}
//                                 renderTags={(tagValue, getTagProps) => {
//                                     return tagValue.map((option, index) => (
//                                         <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />
//                                     ));
//                                 }}
//                                 onChange={(e, newVal) => {
//                                     setSearchQuery(newVal);
//                                 }}
//                                 renderInput={(params) => (
//                                     <TextField
//                                         {...params}
//                                         variant="filled"
//                                         placeholder="Search by notebook, owner, status etc"
//                                         fullWidth
//                                         size="small"
//                                         hiddenLabel
//                                         InputProps={{
//                                             ...params.InputProps,
//                                             startAdornment: (
//                                                 <>
//                                                     <InputAdornment position="start">
//                                                         <img
//                                                             src="/assets/search-normal.svg"
//                                                             alt="Search Icon"
//                                                             style={{ width: "16px", paddingLeft: "4px" }}
//                                                         />
//                                                     </InputAdornment>
//                                                     {params.InputProps.startAdornment}
//                                                 </>
//                                             ),
//                                             disableUnderline: true,
//                                             style: {
//                                                 backgroundColor: "#f4f4f4",
//                                                 minHeight: "45px",
//                                                 width: "500px",
//                                                 borderRadius: "8px",
//                                                 fontFamily: "Gilroy-Regular",
//                                                 fontSize: "14px",
//                                             },
//                                         }}
//                                     />
//                                 )}
//                             />
//                         </Box>
//                     </Box>
//                     <Box m={2}>
//                         <Accordion
//                             defaultExpanded={filteredPendingNotebooks.length > 0}
//                             elevation={0}
//                             sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                             disabled={filteredPendingNotebooks.length === 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMore />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }}/>
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Pending Notebooks ({filteredPendingNotebooks.length})
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails>
//                                 {filteredPendingNotebooks.length > 0 && (
//                                     <NotebooksRequestTable notebooks={filteredPendingNotebooks} />
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>
                        
//                         <Accordion
//                             defaultExpanded={true}
//                             elevation={0}
//                             sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                             disabled={filteredActiveNotebooks.length === 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMore />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Active Notebooks ({filteredActiveNotebooks.length})
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails>
//                                 {filteredActiveNotebooks.length > 0 && (
//                                     <NotebooksRequestTable notebooks={filteredActiveNotebooks} />
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>

//                         <Accordion
//                             defaultExpanded={filteredRejectedNotebooks.length > 0}
//                             elevation={0}
//                             sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                             disabled={filteredRejectedNotebooks.length === 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMore />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Rejected Notebooks ({filteredRejectedNotebooks.length})
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails>
//                                 {filteredRejectedNotebooks.length > 0 && (
//                                     <NotebooksRequestTable notebooks={filteredRejectedNotebooks} />
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>
//                     </Box>
//                 </Container>
//             </Box>
//         </Box>
//     );
// };

// export default NotebooksRequestsPage;




// import React, { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import {
//     Box,
//     Container,
//     Typography,
//     Divider,
//     TextField,
//     Autocomplete,
//     Chip,
//     InputAdornment,
//     Accordion,
//     AccordionSummary,
//     AccordionDetails,
//     IconButton,
// } from "@mui/material";

// import queryGraphql from "../../../helpers/ApiInterface"; // Assuming path
// import { ExpandMore } from "@mui/icons-material";
// import MainDrawer from "../../MainDrawer"; // Assuming path
// import NotebooksRequestTable from "./NotebooksRequestsTable"; // Import new table
// import MiniBot from "../../MiniBot"; // Assuming path

// // Interface for search options, tailored for Notebooks
// interface SearchOptions {
//     chipLabel: string;
//     dropdownLabel: string;
//     category: string;
//     groupBy: string;
// }

// // Interface for the processed notebook data structure used in this page
// interface ProcessedNotebook {
//     id: string;
//     notebookName: string;
//     status: string;
//     createdAt: string | null;
//     actionedAt: string | null;
//     notebookOwnerName: string | null | undefined;
//     notebookOwnerAvatar?: string; // Avatar might not be in the query, plan for fallback
//     notebookApproverName: string | null | undefined;
//     reasonForRejection?: string;
// }

// let searchOptions: SearchOptions[] = [];

// const NotebooksRequestsPage: React.FC = () => {
//     const [notebooks, setNotebooks] = useState<ProcessedNotebook[]>([]);
//     const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
//     const navigate = useNavigate();

//     React.useEffect(() => {
//         const fetchData = async () => {
//             try {
//                 // GraphQL Query to get notebooks, same as in Notebooks.tsx
//                 const data = await queryGraphql(`
//                     query GetNotebooks {
//                       getNotebooks {
//                         id
//                         name
//                         status
//                         reasonForRejection
//                         createdAt
//                         updatedAt
//                         solutionOwner {
//                           id
//                           name
//                         }
//                         solutionApprover {
//                           id
//                           name
//                         }
//                       }
//                     }
//                 `);

//                 const rawNotebooksData = data.getNotebooks || data.getNotebooks || [];

//                 // Process the raw data into the structure needed for the requests view
//                 let notebooksArr: ProcessedNotebook[] = rawNotebooksData.map((nb: any) => {
//                     return {
//                         id: nb.id,
//                         notebookName: nb.name,
//                         status: nb.status ? nb.status.toLowerCase() : 'pending',
//                         createdAt: nb.createdAt ? new Date(Number(nb.createdAt)).toISOString() : null,
//                         actionedAt: nb.updatedAt ? new Date(Number(nb.updatedAt)).toISOString() : null,
//                         notebookOwnerName: nb.solutionOwner.name,
//                         // Avatar is not in the query, so it will be undefined. The table can handle this.
//                         notebookOwnerAvatar: undefined,
//                         notebookApproverName: nb.solutionApprover?.name,
//                         reasonForRejection: nb.reasonForRejection,
//                     };
//                 });
//                 setNotebooks(notebooksArr);

//                 // Generate search options from the processed notebook data
//                 searchOptions = notebooksArr
//                     .map((nb: ProcessedNotebook) => {
//                         const options = [
//                             {
//                                 category: "notebookName",
//                                 groupBy: "NOTEBOOK NAME",
//                                 chipLabel: `Notebook: ${nb.notebookName}`,
//                                 dropdownLabel: nb.notebookName,
//                             },
//                         ];
//                         if (nb.notebookOwnerName) {
//                             options.push({
//                                 category: "notebookOwnerName",
//                                 groupBy: "OWNER",
//                                 chipLabel: `Owner: ${nb.notebookOwnerName}`,
//                                 dropdownLabel: nb.notebookOwnerName,
//                             });
//                         }
//                         if (nb.notebookApproverName) {
//                             options.push({
//                                 category: "notebookApproverName",
//                                 groupBy: "APPROVER",
//                                 chipLabel: `Approver: ${nb.notebookApproverName}`,
//                                 dropdownLabel: nb.notebookApproverName,
//                             });
//                         }
//                         if (nb.status) {
//                             options.push({
//                                 category: "status",
//                                 groupBy: "STATUS",
//                                 chipLabel: `Status: ${nb.status.charAt(0).toUpperCase() + nb.status.slice(1)}`,
//                                 dropdownLabel: nb.status.charAt(0).toUpperCase() + nb.status.slice(1),
//                             });
//                         }
//                         return options;
//                     })
//                     .flat();

//                 // Deduplicate search options
//                 searchOptions = searchOptions.filter(
//                     (obj, index, self) =>
//                         obj.dropdownLabel &&
//                         index ===
//                         self.findIndex(
//                             (t) => t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
//                         )
//                 );

//                 // Sort search options
//                 searchOptions.sort((a, b) => {
//                     if (a.groupBy > b.groupBy) return 1;
//                     if (a.groupBy < b.groupBy) return -1;
//                     if (a.dropdownLabel > b.dropdownLabel) return 1;
//                     if (a.dropdownLabel < b.dropdownLabel) return -1;
//                     return 0;
//                 });

//             } catch (error) {
//                 console.log("Error fetching notebooks for request page:", error);
//             }
//         };

//         fetchData();
//     }, []);

//     // Filter notebooks based on the search query
//     const filteredNotebooks =
//         searchQuery.length === 0
//             ? notebooks
//             : notebooks.filter(
//                 (notebook: ProcessedNotebook) =>
//                     (searchQuery.filter((e) => e.category === "notebookName").length > 0
//                         ? searchQuery
//                                 .filter((e) => e.category === "notebookName")
//                                 .map((e) => e.dropdownLabel?.toLowerCase())
//                                 .includes(notebook.notebookName?.toLowerCase())
//                         : true) &&
//                     (searchQuery.filter((e) => e.category === "notebookOwnerName").length > 0
//     // Check if the notebook has an owner name before trying to find it in the search query
//     ? (notebook.notebookOwnerName && 
//         searchQuery
//             .filter((e) => e.category === "notebookOwnerName")
//             .map((e) => e.dropdownLabel?.toLowerCase())
//             // Now that we know notebookOwnerName exists, we don't need optional chaining here
//             .includes(notebook.notebookOwnerName.toLowerCase()))
//     : true
// ) &&
// (searchQuery.filter((e) => e.category === "notebookApproverName").length > 0
//     // Apply the same logic for the approver name
//     ? (notebook.notebookApproverName &&
//         searchQuery
//             .filter((e) => e.category === "notebookApproverName")
//             .map((e) => e.dropdownLabel?.toLowerCase())
//             .includes(notebook.notebookApproverName.toLowerCase()))
//     : true
// ) &&
//                     (searchQuery.filter((e) => e.category === "status").length > 0
//                         ? searchQuery
//                                 .filter((e) => e.category === "status")
//                                 .map((e) => e.dropdownLabel?.toLowerCase())
//                                 .includes(notebook.status?.toLowerCase())
//                         : true)
//             );

//     // Group filtered notebooks by status for accordions
//     const pendingStatuses = ['pending', 'submitted', 'provisioning'];
//     const activeStatuses = ['active', 'approved', 'success'];
//     const rejectedStatuses = ['rejected', 'failed'];

//     const filteredPendingNotebooks = filteredNotebooks.filter(
//         (nb: ProcessedNotebook) => pendingStatuses.includes(nb.status)
//     );
//     const filteredActiveNotebooks = filteredNotebooks.filter(
//         (nb: ProcessedNotebook) => activeStatuses.includes(nb.status)
//     );
//     const filteredRejectedNotebooks = filteredNotebooks.filter(
//         (nb: ProcessedNotebook) => rejectedStatuses.includes(nb.status)
//     );

//     return (
//         <Box sx={{ display: "flex" }}>
//             <MainDrawer index={"requests-notebooks"} />
//             <MiniBot />
//             <Box
//                 component="main"
//                 sx={{
//                     flex: 1,
//                     bgcolor: "#fafafa",
//                     minHeight: "100vh",
//                 }}
//             >
//                 <Container maxWidth={false} disableGutters>
//                     <Box bgcolor="#FFFFFF" p={2} mb="18px">
//                         <Box
//                             sx={{
//                                 display: "flex",
//                                 justifyContent: "space-between",
//                                 alignItems: "center",
//                                 marginY: 1,
//                                 marginLeft: 2,
//                             }}
//                         >
//                             <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold">
//                                 Requests
//                             </Typography>
//                              <IconButton
//                                 aria-label="Help" 
//                                 onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
//                                 sx={{
//                                     padding: 0, border: 'none',  mr:2,
//                                     '&:hover': { backgroundColor: 'action.hover' },
//                                 }}
//                               >
//                                 <Box component="img" src="/assets/Help.svg" alt="Help Icon" sx={{ width: 40, height: 40, display: 'block' }} />
//                             </IconButton>
//                         </Box>
//                         <Divider sx={{ marginY: 1 }} />
//                         <Box
//                             sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mx: 2 }}
//                         >
//                             <Box>
//                                 <Typography
//                                     fontFamily="Gilroy-SemiBold"
//                                     fontSize="16px"
//                                 >{`NOTEBOOKS (${notebooks.length})`}</Typography>
//                                 <Typography
//                                     fontFamily="Gilroy-Regular"
//                                     fontSize="14px"
//                                 >{`View all the requests raised for Notebooks`}</Typography>
//                             </Box>
//                             <Autocomplete
//                                 multiple
//                                 options={searchOptions}
//                                 filterSelectedOptions
//                                 getOptionLabel={(option) => option.dropdownLabel}
//                                 defaultValue={[]}
//                                 groupBy={(option) => option.groupBy}
//                                 renderTags={(tagValue, getTagProps) => {
//                                     return tagValue.map((option, index) => (
//                                         <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} />
//                                     ));
//                                 }}
//                                 onChange={(e, newVal) => {
//                                     setSearchQuery(newVal);
//                                 }}
//                                 renderInput={(params) => (
//                                     <TextField
//                                         {...params}
//                                         variant="filled"
//                                         placeholder="Search by notebook, owner, status etc"
//                                         fullWidth
//                                         size="small"
//                                         hiddenLabel
//                                         InputProps={{
//                                             ...params.InputProps,
//                                             startAdornment: (
//                                                 <>
//                                                     <InputAdornment position="start">
//                                                         <img
//                                                             src="/assets/search-normal.svg"
//                                                             alt="Search Icon"
//                                                             style={{ width: "16px", paddingLeft: "4px" }}
//                                                         />
//                                                     </InputAdornment>
//                                                     {params.InputProps.startAdornment}
//                                                 </>
//                                             ),
//                                             disableUnderline: true,
//                                             style: {
//                                                 backgroundColor: "#f4f4f4",
//                                                 minHeight: "45px",
//                                                 width: "500px",
//                                                 borderRadius: "8px",
//                                                 fontFamily: "Gilroy-Regular",
//                                                 fontSize: "14px",
//                                             },
//                                         }}
//                                     />
//                                 )}
//                             />
//                         </Box>
//                     </Box>
//                     <Box m={2}>
//                         <Accordion
//                             defaultExpanded={filteredPendingNotebooks.length > 0}
//                             elevation={0}
//                             sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                             disabled={filteredPendingNotebooks.length === 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMore />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }}/>
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Pending Notebooks ({filteredPendingNotebooks.length})
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails>
//                                 {filteredPendingNotebooks.length > 0 && (
//                                     <NotebooksRequestTable notebooks={filteredPendingNotebooks} />
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>
                        
//                         <Accordion
//                             defaultExpanded={true}
//                             elevation={0}
//                             sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                             disabled={filteredActiveNotebooks.length === 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMore />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Active Notebooks ({filteredActiveNotebooks.length})
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails>
//                                 {filteredActiveNotebooks.length > 0 && (
//                                     <NotebooksRequestTable notebooks={filteredActiveNotebooks} />
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>

//                         <Accordion
//                             defaultExpanded={filteredRejectedNotebooks.length > 0}
//                             elevation={0}
//                             sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
//                             disabled={filteredRejectedNotebooks.length === 0}
//                         >
//                             <AccordionSummary expandIcon={<ExpandMore />}>
//                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                     <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
//                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
//                                         Rejected Notebooks ({filteredRejectedNotebooks.length})
//                                     </Typography>
//                                 </Box>
//                             </AccordionSummary>
//                             <AccordionDetails>
//                                 {filteredRejectedNotebooks.length > 0 && (
//                                     <NotebooksRequestTable notebooks={filteredRejectedNotebooks} />
//                                 )}
//                             </AccordionDetails>
//                         </Accordion>
//                     </Box>
//                 </Container>
//             </Box>
//         </Box>
//     );
// };

// export default NotebooksRequestsPage;

// import { Box, Typography } from "@mui/material";
// import React, { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import DashBoardPage from "../../DashBoardPage";
// import MainDrawer from "../../MainDrawer";
// import ComingSoonPage from "../../ComingSoonPage";
// const NotebooksRequestsPage: React.FC = () => {
//     return(<Box sx={{ display: "flex"}}>
//     <MainDrawer index={"requests-notebooks"} />
// {/* Helllo */}
//     <Box sx={{display:"flex" , alignItems:"center", justifyContent:'center' ,flexDirection: 'row', height:"100%", width:"100%"}}>
//  {/* <Typography fontFamily="Gilroy-Bold" fontSize="36px"  textAlign="center"> Coming Soon!!</Typography> */}
//  <ComingSoonPage/>
//     </Box>
//     </Box>)
// }
// export default NotebooksRequestsPage;